import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { parseContactBookPage, type WebContactBookPage } from './contact-book-page';
import { queryClient, queryKeys } from './query-client';

export { parseContactBookPage, type WebContactBookPage } from './contact-book-page';

// The contacts screen's data layer. ONE call — `/api/contact-book`, proxying symphony-bff's
// ListContacts — backs both the list and a contact's detail, because every entry already carries the
// person's threads and their whole record. Opening a contact is a client-side selection, not a fetch.
//
// Types are declared here rather than imported from common-lib for the same reason `lib/routines.ts`
// declares its own: importing them drags this module's type graph into the ESM-only chain that breaks
// jest in CI.

/** One outreach thread on a contact. Direction is which list it sits in, not a field. */
export interface WebOutreachThread {
  outreachId: string;
  channel?: string;
  lastMessage?: string;
  updatedAt?: string;
  waitingOnMe?: boolean;
  /** Lowercased OutreachStatus. Empty means the outreach carries none — not that it is done. */
  status?: string;
  createdAt?: string;
  /**
   * What the outreach was about, from the message metadata the assistant wrote when composing it.
   * Often EMPTY — the in-app channel writes none — so a caller falls back to the creation date.
   */
  subject?: string;
  /**
   * The thread itself, oldest first. Present only on the person-scoped read, so the list's rows
   * carry an empty array and quote `lastMessage` instead.
   */
  messages?: WebOutreachMessage[];
}

/** One message inside a thread. `fromMe` is resolved server-side against the caller's own side. */
export interface WebOutreachMessage {
  text?: string;
  sentAt?: string;
  fromMe?: boolean;
  attachments?: WebOutreachAttachment[];
  /**
   * Who wrote it, from the message's own envelope. Present where `fromMe` cannot answer: older
   * messages carry no direction, so they are attributed to the other party and would sit on the
   * wrong side of a chat view. Empty when the body carries no envelope.
   */
  senderName?: string;
}

/** A file sent with an outreach message. */
export interface WebOutreachAttachment {
  fileName?: string;
  mimeType?: string;
  url?: string;
  /** Absent whenever the sender did not know it — which is every attachment in production today. */
  size?: number | string;
}

/** One way to reach a person, already resolved to what the row should say. */
export interface WebContactChannel {
  kind?: string;
  label?: string;
  value?: string;
  preferred?: boolean;
}

/** How a person appears in a list. Cheap enough to render anywhere. */
export interface WebContactCard {
  personId?: string;
  displayName?: string;
  labels?: string[];
  lastTouchAt?: string;
  needsAttention?: boolean;
  attentionSummary?: string;
  openLoopCount?: number;
  /**
   * The photo to render for this contact, RESOLVED server-side by symphony-bff (`ContactCard.effectiveAvatarUrl`):
   * the viewer's own per-contact override (`Person.customAvatarUrl`) if set, else the matched recipient's
   * self-photo, else the CRM photo. Empty when none resolves — the row falls back to initials. Read-only;
   * the override is written via `/api/contact-channel` (`customAvatarUrl`). Arrives structurally over
   * `/api/contact-book` (a straight pass-through of the BFF row), so no mapping step carries it.
   */
  effectiveAvatarUrl?: string;
}

/** A person and everything the user has exchanged with them, list row and detail in one. */
export interface WebContact {
  contact?: WebContactCard;
  outbound?: WebOutreachThread[];
  inbound?: WebOutreachThread[];
  lastActivityAt?: string;
  waitingOnMe?: boolean;
  waitingCount?: number;
  channels?: WebContactChannel[];
  facts?: string[];
  openLoops?: string[];
  source?: string;
  cadenceDays?: number;
  recipientId?: string;
  /**
   * What this person's OWN assistant is called, off their Recipient.
   *
   * A received outreach message is written by their AGENT on their behalf, not typed by them, so
   * this is the honest attribution in the thread. Empty when they run no assistant — the common
   * case, since most counterparties are not Symphony users — and the label falls back to the person.
   */
  agentName?: string;
}

export type ContactBookState =
  | { kind: 'loading' }
  /** `retry` re-asks the same browse. Without it the only way out of a failed LIST was reloading the
   *  page, while the native list screens offer Retry — the asymmetry this type's consumers created. */
  | { kind: 'error'; message: string; retry: () => void }
  | { kind: 'ready'; contacts: WebContact[] };

async function fetchContacts(search: string): Promise<WebContact[]> {
  const res = await apiFetch(`${API_BASE}/api/contact-book`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(search ? { search } : {}),
  });
  if (!res.ok) throw new Error(`contact-book: ${res.status}`);
  const data = (await res.json()) as { contacts?: WebContact[] };
  // A row with no card cannot be drawn and cannot be opened; the server should never send one, but a
  // list that silently renders a blank row is harder to diagnose than one that never shows it.
  return (data.contacts ?? []).filter((entry) => !!entry?.contact);
}

/** Warm the account's contact book into the React Query cache on app mount, so the Your Agents
 *  Contacts summary tile doesn't sit on a spinner. Best-effort; swallows errors. */
export function prefetchContactBook(): void {
  void queryClient.prefetchQuery({ queryKey: queryKeys.contactBook(''), queryFn: () => fetchContacts('') }).catch(() => {});
}

/**
 * The contact list.
 *
 * A non-empty `search` is a different question, not a filter over the same answer: it resolves
 * against the whole people book and finds people with no outreach at all, where the empty-search
 * browse only ever returns people you have exchanged with. So the two are cached separately, keyed
 * on the query.
 */
export function useContactBook(
  search: string,
  options: {
    /** False reads nothing (a caller that has the list from somewhere else). Default true. */
    enabled?: boolean;
    /** Default true. The Outreach Contacts tab's server search passes false — see below. */
    keepPreviousData?: boolean;
  } = {},
): ContactBookState {
  const query = useQuery({
    queryKey: queryKeys.contactBook(search),
    queryFn: () => fetchContacts(search),
    enabled: options.enabled ?? true,
    // A search is typed a character at a time; keeping the previous list on screen while the next
    // one loads stops the whole page flashing between keystrokes.
    //
    // The Outreach Contacts tab turns it OFF: its search is a server question per query, and both
    // phones show their loader until THIS query answers, so a result for "ann" never sits under "anna"
    // (and a stranger's row never sits under the query that does not match them).
    //
    // `keepPreviousData`, NOT v5's `placeholderData: (previous) => previous` — this app is on
    // react-query v4, where that option takes a value rather than a function and the mismatch falls
    // through every overload into an error that names `queryKey` instead.
    keepPreviousData: options.keepPreviousData ?? true,
  });

  if (query.data) return { kind: 'ready', contacts: query.data };
  if (query.error) {
    return { kind: 'error', message: (query.error as Error).message, retry: () => void query.refetch() };
  }
  return { kind: 'loading' };
}

async function fetchContactBookPage(bookCursor: string | undefined): Promise<WebContactBookPage> {
  const res = await apiFetch(`${API_BASE}/api/contact-book`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    // Page 1 is the browse WITH the book's first page; every later page is its cursor alone, which the
    // server answers with the book only.
    body: JSON.stringify(bookCursor ? { bookCursor } : { includeBook: true }),
  });
  if (!res.ok) throw new Error(`contact-book: ${res.status}`);
  return parseContactBookPage(await res.json());
}

/**
 * TanStack refetches every retained cursor page in sequence, so a remount or a refocus over a deeply
 * scrolled book would re-walk it page by page. Past this many pages automatic refetching stops and the
 * book stays as it is until the reader leaves and returns or reloads — the same budget `lib/chats.ts`
 * keeps for the chat list, for the same reason.
 */
export const MAX_AUTOMATIC_BOOK_PAGES = 5;
function canAutomaticallyRefreshBook(data: InfiniteData<WebContactBookPage> | undefined): boolean {
  return (data?.pages.length ?? 0) <= MAX_AUTOMATIC_BOOK_PAGES;
}

export type WholeContactBookState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; retry: () => void }
  | {
      kind: 'ready';
      /** The browse — the people behind the caller's recent threads, the same list every surface reads. */
      contacts: WebContact[];
      /** Every book page loaded so far, concatenated. Not yet filtered or ordered — see `bookTail`. */
      book: WebContact[];
      /** True while the book has pages left to read. */
      hasMore: boolean;
      /** True while a later page is in flight. */
      loadingMore: boolean;
      /** True when the last page request failed; the next `loadMore` retries it. */
      loadMoreFailed: boolean;
      /** Page 1's book could not be read and none was held — see `WebContactBookPage.bookUnavailable`. */
      bookUnavailable: boolean;
      /** Re-read from page 1 — the Retry for an unreadable book. */
      retry: () => void;
      /** Read the next page. A no-op while one is in flight or when there is nothing left. */
      loadMore: () => void;
    };

/**
 * The Outreach Contacts tab: the browse, then the whole people book a page at a time.
 *
 * Two caches, deliberately. The BOOK pages live under `queryKeys.contactBookWhole`, outside the
 * `['contact-book']` prefix a send invalidates, because a book row has no threads for a send to
 * change. The HEAD is the ordinary `contactBook('')` entry: page 1 seeds it, the same way the phones
 * write `contactsCache[""]`, so every other surface reading the browse sees the same rows and every
 * invalidation that already re-badges them keeps working here. Its `staleTime` covers the seed, so
 * enabling it once page 1 lands does not read the browse a second time.
 *
 * A cached head paints at once, before the book arrives — the cache-first paint both phones do.
 */
export function useWholeContactBook(options: { enabled?: boolean } = {}): WholeContactBookState {
  const enabled = options.enabled ?? true;
  const book = useInfiniteQuery({
    queryKey: queryKeys.contactBookWhole,
    enabled,
    queryFn: async ({ pageParam }) => {
      const page = await fetchContactBookPage(pageParam as string | undefined);
      if (!pageParam) {
        queryClient.setQueryData(queryKeys.contactBook(''), page.contacts);
        // An unreadable book keeps the first page already held, under the fresh head: the head
        // answered, the book did not, this time — and the refetch then walks on from that page's cursor.
        // With nothing held, the flag stands and the tab shows the read error. Same rule as both phones.
        const held = queryClient.getQueryData<InfiniteData<WebContactBookPage>>(queryKeys.contactBookWhole)?.pages[0];
        if (page.bookUnavailable && held && !held.bookUnavailable) return { ...held, contacts: page.contacts };
      }
      return page;
    },
    getNextPageParam: (last: WebContactBookPage) => last.bookNextCursor,
    staleTime: 30_000,
    refetchOnMount: (query) => canAutomaticallyRefreshBook(query.state.data),
    refetchOnWindowFocus: (query) => canAutomaticallyRefreshBook(query.state.data),
    refetchOnReconnect: (query) => canAutomaticallyRefreshBook(query.state.data),
  });
  const head = useQuery({
    queryKey: queryKeys.contactBook(''),
    queryFn: () => fetchContacts(''),
    // Only after page 1 has answered: before it, a cached head is still READ (a disabled query serves
    // its cache) but not re-fetched in parallel with the page that is about to seed it.
    enabled: enabled && book.data !== undefined,
    staleTime: 30_000,
  });

  // Flattened once per page arrival, not per render: the caller memoizes its sort on this array's
  // identity, and the book is the one list on the screen that grows without bound.
  const pages = book.data?.pages;
  const flatBook = useMemo(() => (pages ?? []).flatMap((page) => page.book), [pages]);
  const contacts = head.data ?? pages?.[0]?.contacts;
  if (contacts) {
    return {
      kind: 'ready',
      contacts,
      book: flatBook,
      hasMore: !!book.hasNextPage,
      loadingMore: book.isFetchingNextPage,
      // v4 has no `isFetchNextPageError` (that is v5): a failed page leaves the query in `error`
      // with its data kept, and data being present is how this branch was reached.
      loadMoreFailed: book.isError,
      bookUnavailable: book.data?.pages[0]?.bookUnavailable ?? false,
      retry: () => void book.refetch(),
      loadMore: () => {
        if (book.hasNextPage && !book.isFetchingNextPage) void book.fetchNextPage();
      },
    };
  }
  if (book.error) {
    return { kind: 'error', message: (book.error as Error).message, retry: () => void book.refetch() };
  }
  return { kind: 'loading' };
}

/**
 * How one contact is addressed when it is opened.
 *
 * A person in the book opens by `personId`. A counterparty the book knows only by an address — an
 * outreach recipient with no matching Person — has no person id, so it opens by `recipientId`
 * instead. Exactly one is set.
 */
export type ContactId = { personId: string } | { recipientId: string };

/** True for the address-only case — the recipient-keyed read the person endpoint cannot serve. */
export function isRecipientId(id: ContactId): id is { recipientId: string } {
  return 'recipientId' in id;
}

/**
 * The contact-detail route for an identity — RELATIVE, so a raw `<Link href>` must prefix it with the
 * mount base path (`router.push` resolves the mount itself). A recipient carries `?kind=recipient` so
 * the detail page reads its id off the same `[id]` segment but calls the recipient-keyed endpoint —
 * the query-param scope pattern the app already uses for `/tasks?agent=` and `/files?space=`.
 */
export function contactPath(id: ContactId): string {
  return isRecipientId(id)
    ? `/contacts/${encodeURIComponent(id.recipientId)}?kind=recipient`
    : `/contacts/${encodeURIComponent(id.personId)}`;
}

/** The thread route within a contact, carrying the same `kind` discriminator as {@link contactPath}. */
export function contactThreadPath(id: ContactId, outreachId: string): string {
  const contactSegment = isRecipientId(id) ? encodeURIComponent(id.recipientId) : encodeURIComponent(id.personId);
  const outreachSegment = encodeURIComponent(outreachId);
  return isRecipientId(id)
    ? `/contacts/${contactSegment}/${outreachSegment}?kind=recipient`
    : `/contacts/${contactSegment}/${outreachSegment}`;
}

/** Reads the detail/thread route's `[id]` + `kind` query back into an identity. Next decodes the route
 *  param, so this is the inverse of the `encodeURIComponent` in {@link contactPath}. */
export function contactIdFromQuery(id: string, kind: unknown): ContactId {
  return kind === 'recipient' ? { recipientId: id } : { personId: id };
}

/**
 * The per-device "seen" key for a contact row — the person id, or the recipient id for a contact the
 * people book knows only by an address. Keying on it lets a recipient-only row's NEW dot be marked and
 * cleared (mark-all-read); keying on `personId` alone left every such row stuck NEW. 1:1 with the
 * native `seenKey`. Empty only for a row with neither, which the server never sends.
 */
export function contactSeenKey(entry: { contact?: { personId?: string }; recipientId?: string }): string {
  return entry.contact?.personId || entry.recipientId || '';
}

/**
 * One contact by identity WITH the underlying query's load state.
 *
 * {@link useContactEntry} returns only the data, so it reports `undefined` for BOTH the loading and
 * the error cases — a caller that drives load telemetry off `!entry` would sit in "loading" forever
 * when the fetch fails. This exposes the real react-query signals so a `loadFinish` fires on success
 * AND failure. `isLoading` is `status === 'loading'` (true only until the fetch settles either way),
 * and `isError` is the failure branch.
 */
export function useContactEntryQuery(id: ContactId): {
  entry: WebContact | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const recipientId = isRecipientId(id) ? id.recipientId : undefined;
  const personId = isRecipientId(id) ? undefined : id.personId;
  const query = useQuery({
    queryKey: recipientId
      ? queryKeys.contactBookRecipient(recipientId)
      : queryKeys.contactBookPerson(personId),
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/contact-book`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recipientId ? { recipientId } : { personId }),
      });
      if (!res.ok) throw new Error(`contact-book: ${res.status}`);
      const data = (await res.json()) as { contacts?: WebContact[] };
      return data.contacts?.[0];
    },
    enabled: !!(personId || recipientId),
  });
  // `refetch` so a failed read can offer Retry. Without it the only way to re-ask was a full reload,
  // which on the detail and thread pages is the whole screen.
  return {
    entry: query.data ?? undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void query.refetch(),
  };
}

/**
 * One contact by identity — a deep link, which the endpoint answers directly.
 *
 * Both a person id and a recipient id resolve one entry with its threads WHOLE (the list carries
 * only the last-message preview). The two are cached under separate keys, and only one is ever sent.
 */
export function useContactEntry(id: ContactId): WebContact | undefined {
  return useContactEntryQuery(id).entry;
}

/** One contact by person id — the common case, a thin wrapper over {@link useContactEntry}. */
export function useContact(personId: string | undefined): WebContact | undefined {
  return useContactEntry({ personId: personId ?? '' });
}

/**
 * Re-read one contact's card — its channels and threads — after a write that changes what it shows.
 * `useContact`'s cache (`queryKeys.contactBookPerson`) is separate from `queryKeys.outreaches`, so
 * completing a thread (`OutreachNoReplyNeeded` → `/api/outreach-complete`, which only refreshes the
 * latter) left the contact's own thread view still showing it as pending until an unrelated refetch.
 */
export async function refreshContact(personId: string): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookPerson(personId) });
}

/**
 * Re-read EVERYTHING a contact appears in, after a write that can change what any of it says.
 *
 * Reach for this after a SEND. A send is not only a new message: the server reopens a completed
 * outreach on it (`send-outreach-message` advances the status from its post-send read — #10932,
 * #10934), so the thread's turn changes from `closed` to `awaitingReply` and every surface that
 * badges it is now wrong. Refreshing only the entry left the LIST badges stale — the contact row,
 * the Inbox row and their section grouping all read `queryKeys.contactBook`, which is a different
 * cache key from the person/recipient read the viewer uses.
 *
 * The list is invalidated by PREFIX (`['contact-book']`), not `contactBook('')`, because the key
 * carries the search string: a reader who sent from a filtered Inbox would otherwise keep the stale
 * row they were looking at, which is the one case where it is most visible.
 */
export async function refreshContactData(id: ContactId): Promise<void> {
  await Promise.all([
    refreshContactEntry(id),
    queryClient.invalidateQueries({ queryKey: ['contact-book'] }),
  ]);
}

/** The same re-read for a contact opened by identity — person or recipient. */
export async function refreshContactEntry(id: ContactId): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: isRecipientId(id)
      ? queryKeys.contactBookRecipient(id.recipientId)
      : queryKeys.contactBookPerson(id.personId),
  });
}

/**
 * Optimistically append the user's just-sent message onto its thread in the cached contact, so the
 * outreach viewer shows it the instant they hit Send — before the direct send round-trips. Returns a
 * rollback that restores the previous cache; call it if the send fails. On success, `refreshContact`
 * reconciles against the server's own copy (the SAME query key, so the optimistic entry is replaced,
 * never duplicated). `fromMe: true` places it on the user's side (`messageIsMine`).
 */
export function appendOptimisticOutreachMessage(personId: string, outreachId: string, text: string): () => void {
  const key = queryKeys.contactBookPerson(personId);
  const prev = queryClient.getQueryData<WebContact>(key);
  const msg: WebOutreachMessage = { text, sentAt: new Date().toISOString(), fromMe: true };
  const patch = (threads?: WebOutreachThread[]): WebOutreachThread[] | undefined =>
    threads?.map((thread) => (thread.outreachId === outreachId ? { ...thread, messages: [...(thread.messages ?? []), msg] } : thread));
  queryClient.setQueryData<WebContact>(key, (old) => (old ? { ...old, outbound: patch(old.outbound), inbound: patch(old.inbound) } : old));
  return () => queryClient.setQueryData<WebContact>(key, prev);
}

/**
 * Add a missing email or phone to a contact.
 *
 * Goes straight to the People service (see `pages/api/contact-channel.ts` for why there is no BFF
 * hop). The contact cache is invalidated rather than patched: the server unions the new value into
 * the stored list and returns the merged person, so re-reading is both simpler and the only way to
 * be sure the screen shows what was actually saved.
 */
export async function addContactChannel(
  personId: string,
  input: { channel: 'whatsapp' | 'sms' | 'email'; value: string; preferred?: boolean },
): Promise<boolean> {
  // Send the legacy `email`/`phone` fields ALONGSIDE the new `channel`/`value`/`preferred`. The new
  // route reads `channel`/`value` (and prefers them); the currently-DEPLOYED route only understands
  // `email`/`phone`, so this keeps the channel actually getting added until the new route ships. WhatsApp
  // and SMS are both phone numbers on the People model. (`preferred` only takes effect on the new route.)
  const legacy = input.channel === 'email' ? { email: input.value } : { phone: input.value };
  const res = await apiFetch(`${API_BASE}/api/contact-channel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId, ...input, ...legacy }),
  });
  if (!res.ok) return false;
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookPerson(personId) });
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBook('') });
  // A person edit changes what their BOOK row shows too (an avatar, a channel), and the book's pages are
  // deliberately outside the `['contact-book']` prefix a send invalidates — so it is named here, where a
  // person actually changed, and nowhere a send runs.
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookWhole });
  return true;
}

/**
 * Set or clear the viewer's per-contact photo override (Person.customAvatarUrl).
 *
 * Same route and reasoning as `addContactChannel` — straight to the People service via
 * `/api/contact-channel`, mirroring the native `set-person-settings` bridge action. `''` clears the
 * override (the row falls back to the server-resolved avatar). The contact cache is invalidated rather
 * than patched: the BFF re-resolves `effectiveAvatarUrl` from the override, so re-reading is the only
 * way to be sure the screen shows what actually resolved.
 */
export async function setContactAvatar(personId: string, customAvatarUrl: string): Promise<boolean> {
  const res = await apiFetch(`${API_BASE}/api/contact-channel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId, customAvatarUrl }),
  });
  if (!res.ok) return false;
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookPerson(personId) });
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBook('') });
  // A person edit changes what their BOOK row shows too (an avatar, a channel), and the book's pages are
  // deliberately outside the `['contact-book']` prefix a send invalidates — so it is named here, where a
  // person actually changed, and nowhere a send runs.
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookWhole });
  return true;
}
